import {
  paymentMethodDtoPaymentMethodTypeEnum,
  type Invoice,
  type PaymentDto,
  type PaymentMethodDto,
} from 'korint-sdk';
import { formatDate, formatDateWithTime, formatPrice } from '@folio/lib';
import { useMemo, useState } from 'react';
import {
  FileText,
  CreditCard,
  ChevronDown,
  ChevronUp,
  HandCoins,
  Landmark,
  DownloadCloud,
  IterationCw,
} from 'lucide-react';
import {
  Timeline,
  TimelineItem,
  TimelineIcon,
  TimelineHeader,
  TimelineTitle,
  TimelineContent,
  Skeleton,
  Badge,
} from '../../components/ui';
import { useTranslation } from 'react-i18next';
import {
  type InvoiceDocument,
  invoiceDocumentsOf,
  invoiceInsurerLinesOf,
} from './invoiceDocuments';
import type { PlannedRecoveryTask } from '@folio/hooks/usePlannedRecoveryTasks';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

type TimelineEvent =
  | {
      type: 'invoice' | 'credit' | 'payment' | 'manualSettlement';
      createdAt: string;
      data: Invoice | PaymentDto;
      originalInvoiceId?: string;
    }
  | { type: 'plannedRetry'; createdAt: string; data: PlannedRecoveryTask };

// A settlement is dated by its accounting date, which the broker may backdate before the
// invoice even existed; the timeline orders it by when it was recorded, never before the invoice.
const getManualSettlementSortDate = (invoice: Invoice): string => {
  const recordedAt = invoice.paidEventCreatedAt ?? invoice.manualPayment?.appliedAt ?? '';
  return new Date(recordedAt) < new Date(invoice.createdAt) ? invoice.createdAt : recordedAt;
};

interface InvoiceTimelineProps {
  displayInvoice: Invoice;
  originalInvoice?: Invoice | null;
  creditInvoice?: Invoice | null;
  invoicePayments: PaymentDto[];
  paymentMethods: PaymentMethodDto[];
  plannedRetries?: PlannedRecoveryTask[];
  isLoading?: boolean;
}

interface Attribute {
  label: string;
  value: string;
}

const DocumentDownloadLink = ({ document }: { document: InvoiceDocument }) => {
  const { t } = useTranslation();
  const label = document.label ?? t('invoices.module.download');

  return (
    <a
      href={document.signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-muted-foreground hover:text-foreground transition-colors"
      title={label}
      aria-label={label}
    >
      <DownloadCloud className="h-4 w-4" />
    </a>
  );
};

const InvoiceTimelineItemComponent = ({
  invoice,
  isCredit = false,
  originalInvoiceId,
}: {
  invoice: Invoice;
  isCredit?: boolean;
  originalInvoiceId?: string;
}) => {
  const { t } = useTranslation();
  const icon = <FileText className="h-4 w-4" />;

  const invoiceId = invoice.externalId || invoice.id;
  const title = isCredit
    ? t('invoices.timeline.creditInvoiceIssued', { invoiceId })
    : t('invoices.timeline.invoiceIssued', { invoiceId });

  const attributes: Attribute[] = [];
  if (isCredit && originalInvoiceId) {
    attributes.push({ label: t('invoices.timeline.cancelledInvoice'), value: originalInvoiceId });
  }
  const insurerLines = invoiceInsurerLinesOf(invoice, t);

  return (
    <TimelineItem>
      <TimelineIcon
        className={isCredit ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}
      >
        {icon}
      </TimelineIcon>
      <TimelineHeader>
        <Group align="start" justify="between" gap={0} className="flex-1">
          <div className="flex-1">
            <TimelineTitle className="text-sm font-normal">{title}</TimelineTitle>
            {attributes.length > 0 && (
              <Stack className="mt-1 gap-0.5">
                {attributes.map((attr, index) => (
                  <div key={index} className="text-xs text-muted-foreground">
                    {attr.label}:{' '}
                    <Text inline weight="medium">
                      {attr.value}
                    </Text>
                  </div>
                ))}
              </Stack>
            )}
            {insurerLines.length > 0 && (
              <Stack className="mt-1 gap-0.5">
                {insurerLines.map(({ insurer, label, amount, document }) => (
                  <Group key={insurer} gap={2} className="text-xs text-muted-foreground">
                    <span>{label}:</span>
                    <Text inline size="xs" weight="medium">
                      {formatPrice(amount)}
                    </Text>
                    {document && <DocumentDownloadLink document={document} />}
                  </Group>
                ))}
              </Stack>
            )}
          </div>
          <Group gap={3} className="ml-4">
            <Text inline size="sm" weight="medium">
              {formatPrice(invoice.total.amountWithFeesAndTaxes)}
            </Text>
            <Text inline size="sm" tone="muted">
              {formatDateWithTime(new Date(invoice.createdAt))}
            </Text>
            {insurerLines.length === 0 &&
              invoiceDocumentsOf(invoice, t).map(document => (
                <DocumentDownloadLink key={document.id} document={document} />
              ))}
          </Group>
        </Group>
      </TimelineHeader>
    </TimelineItem>
  );
};

const ManualSettlementTimelineItemComponent = ({ invoice }: { invoice: Invoice }) => {
  const { t } = useTranslation();
  const { manualPayment } = invoice;

  if (!manualPayment) return null;

  const invoiceId = invoice.externalId || invoice.id;
  const isRefund = invoice.total.amountWithFeesAndTaxes < 0;
  const title = isRefund
    ? t('invoices.timeline.manualSettlementRefunded', { invoiceId })
    : t('invoices.timeline.manualSettlementPaid', { invoiceId });

  const attributes: Attribute[] = [
    {
      label: t('invoices.timeline.paymentMethod'),
      value: t(`invoices.manualMark.method.${manualPayment.method}`),
    },
    ...(invoice.paidEventCreatedAt
      ? [
          {
            label: t('invoices.timeline.manualSettlementRecordedAt'),
            value: formatDateWithTime(new Date(invoice.paidEventCreatedAt)),
          },
        ]
      : []),
  ];

  return (
    <TimelineItem>
      <TimelineIcon className="bg-green-100 text-green-600">
        <HandCoins className="h-4 w-4" />
      </TimelineIcon>
      <TimelineHeader>
        <Group align="start" justify="between" gap={0} className="flex-1">
          <div className="flex-1">
            <TimelineTitle className="text-sm font-normal">{title}</TimelineTitle>
            <Stack className="mt-1 gap-0.5">
              {attributes.map((attr, index) => (
                <div key={index} className="text-xs text-muted-foreground">
                  {attr.label}:{' '}
                  <Text inline weight="medium">
                    {attr.value}
                  </Text>
                </div>
              ))}
            </Stack>
          </div>
          <Group gap={3} className="ml-4">
            <Text inline size="sm" weight="medium">
              {formatPrice(invoice.total.amountWithFeesAndTaxes)}
            </Text>
            <Text inline size="sm" tone="muted">
              {formatDate(new Date(manualPayment.appliedAt))}
            </Text>
          </Group>
        </Group>
      </TimelineHeader>
    </TimelineItem>
  );
};

const PlannedRetryTimelineItemComponent = ({
  whenAt,
  invoice,
}: {
  whenAt: string;
  invoice: Invoice;
}) => {
  const { t, i18n } = useTranslation();

  return (
    <TimelineItem>
      <TimelineIcon className="border-[1.5px] border-dashed border-border bg-background text-muted-foreground">
        <IterationCw className="h-4 w-4" />
      </TimelineIcon>
      <TimelineHeader>
        <Group align="start" justify="between" gap={0} className="flex-1">
          <div className="flex-1">
            <TimelineTitle className="text-sm font-normal">
              {t('invoices.timeline.plannedRetry')}
            </TimelineTitle>
            <Badge
              variant="secondary"
              className="mt-1 bg-yellow-100 font-normal text-yellow-700 hover:bg-yellow-100"
            >
              {t('invoices.timeline.pending')}
            </Badge>
          </div>
          <Group gap={3} className="ml-4">
            <Text inline size="sm" weight="medium">
              {formatPrice(invoice.total.amountWithFeesAndTaxes)}
            </Text>
            <Text inline size="sm" tone="muted">
              {t('invoices.timeline.plannedAt', {
                date: formatDate(new Date(whenAt), i18n.language),
              })}
            </Text>
          </Group>
        </Group>
      </TimelineHeader>
    </TimelineItem>
  );
};

const PaymentTimelineItemComponent = ({
  payment,
  paymentMethods,
  invoices,
}: {
  payment: PaymentDto;
  paymentMethods: PaymentMethodDto[];
  invoices: Invoice[];
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const isAutomaticRetry = payment.metadata?.some(
    ({ key, value }) => key === 'automatic' && value === 'true',
  );
  const hasFailureDetails =
    payment.status === 'FAILED' &&
    (payment.cancellationReason || payment.failureCode || payment.failureDeclineCode);

  // Extract invoice IDs from payment metadata (comma-separated)
  const invoiceIdMetadata = payment.metadata?.find(m => m.key === 'invoiceIds');
  const parsedInvoiceIds = invoiceIdMetadata?.value
    ? invoiceIdMetadata.value.split(',').map(id => id.trim())
    : [];

  // Find the matching invoice
  const matchingInvoice = invoices.find(inv => parsedInvoiceIds.includes(inv.id));
  const invoiceExternalId = matchingInvoice?.externalId || matchingInvoice?.id || 'N/A';

  const paymentMethod = paymentMethods.find(pm => pm.id === payment.paymentMethodId);

  const getPaymentMethodLabel = (method: PaymentMethodDto | undefined) => {
    if (!method) return t('invoices.timeline.paymentMethodUnknown');

    if (method.paymentMethodType === paymentMethodDtoPaymentMethodTypeEnum.sepa_debit) {
      return t('invoices.timeline.sepaEndingIn', { last4: method.last4 });
    }
    if (method.paymentMethodType === paymentMethodDtoPaymentMethodTypeEnum.card) {
      return t('invoices.timeline.cardEndingIn', { last4: method.last4 });
    }
    return t('invoices.timeline.other');
  };

  const getPaymentMethodIcon = (method: PaymentMethodDto | undefined) => {
    if (!method) return <CreditCard className="h-4 w-4" />;

    switch (method.paymentMethodType) {
      case paymentMethodDtoPaymentMethodTypeEnum.sepa_debit:
        return <Landmark className="h-4 w-4" />;
      case paymentMethodDtoPaymentMethodTypeEnum.card:
        return <CreditCard className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  const getIconClassName = () => {
    switch (payment.status) {
      case 'SUCCESSFUL':
        return 'bg-green-100 text-green-600';
      case 'FAILED':
      case 'DISPUTED':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const iconClassName = getIconClassName();

  return (
    <TimelineItem>
      <TimelineIcon className={iconClassName}>{getPaymentMethodIcon(paymentMethod)}</TimelineIcon>
      <TimelineHeader>
        <Group align="start" justify="between" gap={0} className="flex-1">
          <div className="flex-1">
            {hasFailureDetails ? (
              <button
                type="button"
                className="flex cursor-pointer items-center gap-2 text-left"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <TimelineTitle className="text-sm font-normal">
                  {t([
                    `invoices.timeline.paymentStatus.${payment.status}`,
                    'invoices.timeline.payment',
                  ])}
                </TimelineTitle>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ) : (
              <Group>
                <TimelineTitle className="text-sm font-normal">
                  {t([
                    `invoices.timeline.paymentStatus.${payment.status}`,
                    'invoices.timeline.payment',
                  ])}
                </TimelineTitle>
              </Group>
            )}
            <Stack className="mt-1 gap-0.5">
              {isAutomaticRetry && (
                <Badge variant="secondary" className="mb-1 font-normal">
                  {t('invoices.recovery.automaticRetry')}
                </Badge>
              )}
              <div className="text-xs text-muted-foreground">
                {t('invoices.timeline.invoice')}:{' '}
                <Text inline weight="medium">
                  {invoiceExternalId}
                </Text>
              </div>
              <div className="text-xs text-muted-foreground">
                {t('invoices.timeline.paymentMethod')}:{' '}
                <Text inline weight="medium">
                  {getPaymentMethodLabel(paymentMethod)}
                </Text>
              </div>
            </Stack>
          </div>
          <Group gap={3} className="ml-4">
            <Text inline size="sm" weight="medium">
              {formatPrice(payment.amountInEuroCents)}
            </Text>
            <Text inline size="sm" tone="muted">
              {formatDateWithTime(new Date(payment.createdAt))}
            </Text>
          </Group>
        </Group>
      </TimelineHeader>
      {hasFailureDetails && isExpanded && (
        <TimelineContent>
          <div className="mt-2 rounded-md bg-red-50 p-3 text-xs">
            {payment.cancellationReason && (
              <>
                <Text weight="medium" className="mb-1 text-red-900">
                  {t('invoices.timeline.cancellationReasonLabel')}
                </Text>
                <p className="font-mono text-red-700">{payment.cancellationReason}</p>
              </>
            )}
            {payment.failureCode && (
              <Text weight="medium" className="mt-2 text-red-900">
                {t('invoices.timeline.failureCodeLabel')}{' '}
                <Text inline weight="normal" className="font-mono text-red-700">
                  {payment.failureCode}
                </Text>
              </Text>
            )}
            {payment.failureDeclineCode && (
              <Text weight="medium" className="mt-1 text-red-900">
                {t('invoices.timeline.failureDeclineCodeLabel')}{' '}
                <Text inline weight="normal" className="font-mono text-red-700">
                  {payment.failureDeclineCode}
                </Text>
              </Text>
            )}
          </div>
        </TimelineContent>
      )}
    </TimelineItem>
  );
};

export function InvoiceTimeline({
  displayInvoice,
  originalInvoice,
  creditInvoice,
  invoicePayments,
  paymentMethods,
  plannedRetries = [],
  isLoading,
}: InvoiceTimelineProps) {
  const { t } = useTranslation();

  // Create sorted timeline items
  const timelineItems = useMemo((): TimelineEvent[] => {
    const items: TimelineEvent[] = [];

    // Show original invoice if it exists
    if (originalInvoice) {
      items.push({ type: 'invoice', createdAt: originalInvoice.createdAt, data: originalInvoice });
    }

    // Always show the display invoice if it's different from original and credit
    const isDisplayDifferentFromOriginal =
      !originalInvoice || displayInvoice.id !== originalInvoice.id;
    const isDisplayDifferentFromCredit = !creditInvoice || displayInvoice.id !== creditInvoice.id;
    if (isDisplayDifferentFromOriginal && isDisplayDifferentFromCredit) {
      items.push({ type: 'invoice', createdAt: displayInvoice.createdAt, data: displayInvoice });
    }

    // Show credit invoice if it exists
    if (creditInvoice) {
      items.push({
        type: 'credit',
        createdAt: creditInvoice.createdAt,
        data: creditInvoice,
        originalInvoiceId:
          originalInvoice?.externalId ||
          originalInvoice?.id ||
          displayInvoice.externalId ||
          displayInvoice.id,
      });
    }

    invoicePayments.forEach(payment => {
      items.push({ type: 'payment', createdAt: payment.createdAt, data: payment });
    });

    if (displayInvoice.manualPayment) {
      items.push({
        type: 'manualSettlement',
        createdAt: getManualSettlementSortDate(displayInvoice),
        data: displayInvoice,
      });
    }

    plannedRetries.forEach(plannedRetry => {
      items.push({ type: 'plannedRetry', createdAt: plannedRetry.whenAt, data: plannedRetry });
    });

    return items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [displayInvoice, originalInvoice, creditInvoice, invoicePayments, plannedRetries]);

  return (
    <Stack>
      <Title order={3} size="lg">
        {t('invoices.drawer.timeline')}
      </Title>
      {isLoading ? (
        <Stack gap={3}>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-3/4" />
        </Stack>
      ) : timelineItems.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('invoices.drawer.noHistory')}</div>
      ) : (
        <Timeline>
          {timelineItems.map(item => {
            if (item.type === 'invoice') {
              return (
                <InvoiceTimelineItemComponent
                  key={`invoice-${item.data.id}`}
                  invoice={item.data as Invoice}
                />
              );
            } else if (item.type === 'credit') {
              return (
                <InvoiceTimelineItemComponent
                  key={`credit-${item.data.id}`}
                  invoice={item.data as Invoice}
                  isCredit
                  originalInvoiceId={item.originalInvoiceId}
                />
              );
            } else if (item.type === 'plannedRetry') {
              return (
                <PlannedRetryTimelineItemComponent
                  key={`planned-retry-${item.data.task.name}-${item.data.whenAt}`}
                  whenAt={item.data.whenAt}
                  invoice={displayInvoice}
                />
              );
            } else if (item.type === 'manualSettlement') {
              return (
                <ManualSettlementTimelineItemComponent
                  key={`manual-settlement-${item.data.id}`}
                  invoice={item.data as Invoice}
                />
              );
            } else {
              const invoices = [originalInvoice, creditInvoice, displayInvoice].filter(
                Boolean,
              ) as Invoice[];
              return (
                <PaymentTimelineItemComponent
                  key={`payment-${item.data.id}`}
                  payment={item.data as PaymentDto}
                  paymentMethods={paymentMethods}
                  invoices={invoices}
                />
              );
            }
          })}
        </Timeline>
      )}
    </Stack>
  );
}
